// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Bootstrap, worker wiring, and the single piece of app state.
 *
 * The source of truth for the user's file is the Blob itself, not a decoded
 * copy: every operation re-reads it with `.arrayBuffer()` and transfers that
 * buffer into the worker. Transferring detaches the buffer, so holding one
 * around would mean holding a second copy of a 60 MB file purely to survive
 * the transfer — re-reading is cheaper and cannot go stale.
 */

import './styles/main.css';

import { HOW_IT_WORKS, PRIVACY, DIALECTS, ABOUT } from './copy.ts';
import { bindLogToggle, clearLog, logEvent } from './eventlog.ts';
import { containerRefusal, readFileToBytes, sniffContainer } from './ingest.ts';
import { initGlossary } from './glossary.ts';
import { openModal } from './modal.ts';
import { artefactName } from './ops/graft.ts';
import { bundleArtefact, readBundle, recipeArtefact, reportJson, reportMarkdown } from './ops/report.ts';
import {
  hideError,
  hideProgress,
  renderClearOutput,
  renderGraftOutput,
  renderResults,
  renderShell,
  selectedRecipeIds,
  setProgress,
  setSidecarState,
  showError,
  toast,
  updateGraftButton,
} from './ui.ts';
import type { Artefact, GraftChoice, InspectResult, Recipe, WorkerResponse } from './types.ts';

interface SampleEntry {
  slug: string;
  file: string;
  sidecar?: string;
  title: string;
  blurb: string;
  bytes: number;
}

interface State {
  blob: Blob | null;
  name: string;
  size: number;
  result: InspectResult | null;
  sidecar: { name: string; entries: Array<{ keyword: string; text: string }> } | null;
  busy: boolean;
}

const state: State = { blob: null, name: '', size: 0, result: null, sidecar: null, busy: false };

let worker: Worker | null = null;
let nextId = 1;
const pending = new Map<number, (message: WorkerResponse) => void>();

function getWorker(): Worker {
  if (worker) return worker;
  worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
  worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
    const message = event.data;
    if (message.type === 'progress') {
      setProgress(message.phase, message.done, message.total);
      return;
    }
    if (message.type === 'log') {
      logEvent(message.level, message.message);
      return;
    }
    const resolve = pending.get(message.id);
    if (resolve) {
      pending.delete(message.id);
      resolve(message);
    }
  };
  worker.onerror = () => {
    // A worker that dies takes every in-flight request with it. Fail them all
    // rather than leaving the UI spinning on a promise that can never settle.
    for (const [, resolve] of pending) {
      resolve({ type: 'failed', id: -1, message: 'The background worker stopped unexpectedly. Reload the page and try again.' });
    }
    pending.clear();
    worker?.terminate();
    worker = null;
  };
  return worker;
}

function ask(request: Record<string, unknown> & { op: string; bytes: ArrayBuffer }): Promise<WorkerResponse> {
  const id = nextId++;
  return new Promise((resolve) => {
    pending.set(id, resolve);
    getWorker().postMessage({ ...request, id }, [request.bytes]);
  });
}

/* ── artefact delivery ─────────────────────────────────────────────── */

/** iOS refuses a download from a blob: URL inside an installed home-screen app. */
function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function canShareFiles(files: File[]): boolean {
  return typeof navigator.share === 'function' && navigator.canShare?.({ files }) === true;
}

async function deliver(blob: Blob, name: string) {
  const file = new File([blob], name, { type: blob.type || 'application/octet-stream' });
  if (isStandalone() && canShareFiles([file])) {
    try {
      await navigator.share({ files: [file] });
      logEvent('good', `Shared ${name}.`);
      return;
    } catch (error) {
      if ((error as DOMException)?.name === 'AbortError') return;
      // Fall through to the download, which may still work.
    }
  }
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
  logEvent('good', `Saved ${name}.`);
  toast(`Saved ${name}`);
}

function deliverText(artefact: Artefact) {
  void deliver(new Blob([artefact.text ?? ''], { type: artefact.mime }), artefact.name);
}

async function copyText(text: string, what: string) {
  try {
    await navigator.clipboard.writeText(text);
    logEvent('good', `Copied ${what} to the clipboard.`);
    toast(`Copied ${what}`);
  } catch {
    logEvent('warn', `The browser refused clipboard access, so ${what} was not copied. Open the raw text and copy it by hand.`);
    toast('The browser refused clipboard access');
  }
}

/* ── the pipeline ──────────────────────────────────────────────────── */

function resetView() {
  state.result = null;
  state.sidecar = null;
  const results = document.getElementById('results');
  if (results) {
    results.hidden = true;
    results.replaceChildren();
  }
  hideError();
  hideProgress();
}

function startOver() {
  state.blob = null;
  state.name = '';
  state.size = 0;
  resetView();
  clearLog();
  document.getElementById('intro')?.removeAttribute('hidden');
  document.getElementById('dropzone')?.focus();
}

async function ingest(blob: Blob, name: string, sidecarText?: { name: string; text: string }) {
  if (state.busy) return;
  state.busy = true;
  resetView();

  try {
    const file = blob instanceof File ? blob : new File([blob], name);
    const head = new Uint8Array(await file.slice(0, 16).arrayBuffer());
    const container = sniffContainer(head);
    const refusal = containerRefusal(container);
    if (refusal) {
      logEvent('bad', refusal);
      showError(refusal, 'That is not a PNG');
      return;
    }

    // Enforces the size ceiling and the empty-file guard in one place.
    await readFileToBytes(file);

    state.blob = file;
    state.name = name;
    state.size = file.size;
    logEvent('info', `Opened ${name} (${file.size.toLocaleString()} bytes).`);

    setProgress('Reading the file', 0, 1);
    const response = await ask({ op: 'inspect', bytes: await file.arrayBuffer(), name });
    hideProgress();

    if (response.type === 'failed') {
      logEvent('bad', response.message);
      showError(response.message);
      return;
    }
    if (response.type !== 'inspected') return;

    state.result = response.result;
    document.getElementById('intro')?.setAttribute('hidden', '');
    renderResults(response.result, name, file.size, handlers);

    if (sidecarText) {
      applySidecar(sidecarText.name, sidecarText.text);
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    logEvent('bad', message);
    showError(message);
    hideProgress();
  } finally {
    state.busy = false;
  }
}

function applySidecar(name: string, text: string) {
  try {
    const entries = readBundle(text);
    state.sidecar = { name, entries };
    setSidecarState(name, entries.length);
    logEvent('good', `Added ${name}: ${entries.length} recipe${entries.length === 1 ? '' : 's'} (${entries.map((e) => e.keyword).join(', ')}).`);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    logEvent('warn', message);
    showError(message, 'That recipe file could not be read');
  }
}

function graftChoices(): GraftChoice[] {
  const chosen: GraftChoice[] = [];
  const seen = new Set<string>();
  const picked = selectedRecipeIds();

  for (const recipe of state.result?.recipes ?? []) {
    if (!picked.has(recipe.id) || seen.has(recipe.keyword)) continue;
    seen.add(recipe.keyword);
    chosen.push({ keyword: recipe.keyword, text: recipe.text, origin: recipe.carrier });
  }
  // A sidecar the user added deliberately wins over a copy already in the file.
  for (const entry of state.sidecar?.entries ?? []) {
    const existing = chosen.findIndex((c) => c.keyword === entry.keyword);
    if (existing >= 0) chosen.splice(existing, 1);
    chosen.push({ keyword: entry.keyword, text: entry.text, origin: 'sidecar' });
  }
  return chosen;
}

async function runGraft() {
  if (!state.blob || state.busy) return;
  const choices = graftChoices();
  if (choices.length === 0) return;

  state.busy = true;
  hideError();
  try {
    setProgress('Writing the new chunks', 0, 1);
    const response = await ask({ op: 'graft', bytes: await state.blob.arrayBuffer(), name: state.name, choices });
    hideProgress();

    if (response.type === 'failed') {
      logEvent('bad', response.message);
      showError(response.message, 'The graft did not happen');
      return;
    }
    if (response.type !== 'grafted') return;

    const outName = artefactName(state.name, 'regrafted', 'png');
    const file = new File([response.blob], outName, { type: 'image/png' });
    renderGraftOutput(
      response.verify,
      response.wrote,
      () => void deliver(response.blob, outName),
      canShareFiles([file]) ? () => void deliver(response.blob, outName) : null,
    );
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    logEvent('bad', message);
    showError(message, 'The graft did not happen');
    hideProgress();
  } finally {
    state.busy = false;
  }
}

async function runClear() {
  if (!state.blob || state.busy) return;
  state.busy = true;
  hideError();
  try {
    setProgress('Reading the pixels', 0, 1);
    const response = await ask({ op: 'clear', bytes: await state.blob.arrayBuffer(), name: state.name });
    hideProgress();

    if (response.type === 'failed') {
      logEvent('bad', response.message);
      showError(response.message, 'The pixel plane was not overwritten');
      return;
    }
    if (response.type !== 'cleared') return;

    const outName = artefactName(state.name, 'plane-overwritten', 'png');
    const file = new File([response.blob], outName, { type: 'image/png' });
    renderClearOutput(
      response.changedSamples,
      response.totalSamples,
      response.mode,
      response.sizeDelta,
      () => void deliver(response.blob, outName),
      canShareFiles([file]) ? () => void deliver(response.blob, outName) : null,
    );
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    logEvent('bad', message);
    showError(message, 'The pixel plane was not overwritten');
    hideProgress();
  } finally {
    state.busy = false;
  }
}

/* ── handlers passed into the view ─────────────────────────────────── */

const handlers = {
  onCopy: (recipe: Recipe) => void copyText(recipe.text, recipe.keyword),
  onSave: (recipe: Recipe) => deliverText(recipeArtefact(recipe)),
  onSelectionChange: updateGraftButton,
  onReset: startOver,
  onGraft: () => void runGraft(),
  onClear: () => void runClear(),
  onSaveReport: () => {
    if (!state.result) return;
    deliverText(reportMarkdown(state.result, state.name, state.size, new Date().toISOString()));
    deliverText(reportJson(state.result, state.name, state.size, new Date().toISOString()));
  },
  onSaveBundle: () => {
    if (!state.result) return;
    deliverText(bundleArtefact(state.result, state.name));
  },
  onAddSidecar: () => sidecarInput.click(),
};

/* ── input surfaces ────────────────────────────────────────────────── */

const sidecarInput = document.createElement('input');
sidecarInput.type = 'file';
sidecarInput.accept = 'application/json,.json,.txt,text/plain';
sidecarInput.className = 'visually-hidden';
sidecarInput.addEventListener('change', async () => {
  const file = sidecarInput.files?.[0];
  sidecarInput.value = '';
  if (!file) return;
  if (file.size > 32 * 1024 * 1024) {
    showError('That recipe file is larger than 32 MB, which no generation recipe is.', 'That file is too big');
    return;
  }
  applySidecar(file.name, await file.text());
});

function wireDropzone() {
  const zone = document.getElementById('dropzone');
  const input = document.getElementById('file-input') as HTMLInputElement | null;
  if (!zone || !input) return;

  const pick = () => input.click();
  zone.addEventListener('click', pick);
  zone.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      pick();
    }
  });

  input.addEventListener('change', () => {
    const file = input.files?.[0];
    input.value = '';
    if (file) void ingest(file, file.name);
  });

  let depth = 0;
  const over = (event: DragEvent) => {
    event.preventDefault();
    depth += 1;
    zone.classList.add('is-dragging');
  };
  const leave = (event: DragEvent) => {
    event.preventDefault();
    depth = Math.max(0, depth - 1);
    if (depth === 0) zone.classList.remove('is-dragging');
  };

  // Bound to the window, not the zone: a file dropped anywhere on the page is
  // meant for this tool, and a miss that opens the image in the browser tab
  // loses the user's place.
  window.addEventListener('dragenter', over);
  window.addEventListener('dragover', (event) => event.preventDefault());
  window.addEventListener('dragleave', leave);
  window.addEventListener('drop', (event) => {
    event.preventDefault();
    depth = 0;
    zone.classList.remove('is-dragging');
    const file = event.dataTransfer?.files?.[0];
    if (file) void ingest(file, file.name);
  });

  document.addEventListener('paste', (event) => {
    const file = event.clipboardData?.files?.[0];
    if (!file) return;
    event.preventDefault();
    void ingest(file, file.name || 'pasted-image.png');
  });
}

async function wireSamples() {
  const host = document.getElementById('samples');
  if (!host) return;
  let samples: SampleEntry[];
  try {
    samples = (await (await fetch('samples/index.json')).json()) as SampleEntry[];
  } catch {
    host.hidden = true;
    return;
  }

  for (const sample of samples) {
    const pill = document.createElement('button');
    pill.type = 'button';
    pill.className = 'sample-pill';
    pill.textContent = sample.title;
    pill.title = sample.blurb;
    pill.addEventListener('click', async (event) => {
      // The pills sit beside the drop zone rather than inside it, but stopping
      // here costs nothing and guarantees a tap can never also open the picker.
      event.stopPropagation();
      try {
        logEvent('info', `Loading the sample: ${sample.title}.`);
        const blob = await (await fetch(`samples/${sample.file}`)).blob();
        let sidecar: { name: string; text: string } | undefined;
        if (sample.sidecar) {
          sidecar = { name: sample.sidecar, text: await (await fetch(`samples/${sample.sidecar}`)).text() };
        }
        // Straight down the same path a real drop takes — there is no
        // sample-only branch anywhere in this file.
        await ingest(new File([blob], sample.file, { type: 'image/png' }), sample.file, sidecar);
      } catch {
        showError('That sample could not be loaded. If you are offline and this is your first visit, it has not been cached yet.', 'The sample did not load');
      }
    });
    host.append(pill);
  }
}

function wireModals() {
  const bodies: Record<string, [string, string]> = {
    how: ['How regraft works', HOW_IT_WORKS],
    dialects: ['What each generator writes', DIALECTS],
    privacy: ['Privacy & threat model', PRIVACY],
    about: ['About regraft', ABOUT],
  };
  document.addEventListener('click', (event) => {
    const trigger = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-modal]');
    if (!trigger) return;
    const entry = bodies[trigger.dataset.modal ?? ''];
    if (entry) openModal(entry[0], entry[1]);
  });
}

function wireKeyboard() {
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || event.metaKey || event.ctrlKey || event.altKey) return;
    const target = event.target as HTMLElement | null;
    if (target && (target.tagName === 'BUTTON' || target.tagName === 'INPUT' || target.tagName === 'A')) return;
    if (document.querySelector('.modal-backdrop, .drawer-backdrop')) return;
    const primary = document.getElementById('graft-btn') as HTMLButtonElement | null;
    if (primary && !primary.disabled) {
      event.preventDefault();
      primary.click();
    }
  });
}

/* ── boot ──────────────────────────────────────────────────────────── */

function boot() {
  const root = document.getElementById('app');
  if (!root) return;
  renderShell(root);

  const live = document.createElement('div');
  live.id = 'live-region';
  live.className = 'visually-hidden';
  live.setAttribute('role', 'status');
  live.setAttribute('aria-live', 'polite');
  root.append(live, sidecarInput);

  initGlossary();
  wireDropzone();
  wireModals();
  wireKeyboard();
  void wireSamples();

  const toggle = document.getElementById('log-toggle');
  const count = document.getElementById('log-count');
  if (toggle && count) bindLogToggle(toggle, count);

  document.getElementById('error-retry')?.addEventListener('click', startOver);

  logEvent('info', 'Ready. Nothing has left this device, and nothing will.');
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

// Silences the unused-import check for a type used only in signatures above.
export type { InspectResult };
